import { formatIsoDateOnlyBR } from '@/utils/date'
import {
  EmploymentDeclarationKind,
  ProfessionalDocumentPendingReason,
  ProfessionalDocumentRejectionReason,
  ProfessionalDocumentStatus,
  ProfessionalDocumentVersionSource
} from '@/types/professional-documents.types'
import type {
  DocumentHistoryEvent,
  DocumentHistoryKind,
  DocumentRejection,
  EmploymentDeclarationEntry
} from '@/types/professional-documents.types'

/** Mesmos rótulos do formulário do app (`declarationForm.ts`), para gestor e médico lerem igual. */
export const EMPLOYMENT_DECLARATION_KIND_LABEL: Record<EmploymentDeclarationKind, string> = {
  [EmploymentDeclarationKind.SPDM]: 'SPDM',
  [EmploymentDeclarationKind.PUBLIC_SERVANT_MUNICIPAL]: 'Serviço público municipal',
  [EmploymentDeclarationKind.PUBLIC_SERVANT_STATE]: 'Serviço público estadual',
  [EmploymentDeclarationKind.PUBLIC_SERVANT_FEDERAL]: 'Serviço público federal',
  [EmploymentDeclarationKind.OTHER]: 'Outro'
}

/** "desde 01/01/2025", "até 30/06/2026", "01/01/2025 a 30/06/2026" ou "" sem datas. */
export function declarationPeriodText(entry: EmploymentDeclarationEntry): string {
  const start = entry.startDate ? formatIsoDateOnlyBR(entry.startDate) : null
  const end = entry.endDate ? formatIsoDateOnlyBR(entry.endDate) : null
  if (start && end) return `${start} a ${end}`
  if (start) return `desde ${start}`
  if (end) return `até ${end}`
  return ''
}

export const DOCUMENT_STATUS_LABEL: Record<ProfessionalDocumentStatus, string> = {
  [ProfessionalDocumentStatus.PENDING]: 'Pendente',
  [ProfessionalDocumentStatus.IN_REVIEW]: 'Em Análise',
  [ProfessionalDocumentStatus.CONCLUDED]: 'Concluído'
}

/** 5 motivos padrão + "Outro" (D21). */
export const REJECTION_REASON_OPTIONS: {
  value: ProfessionalDocumentRejectionReason
  label: string
}[] = [
  { value: ProfessionalDocumentRejectionReason.ILLEGIBLE, label: 'Documento ilegível' },
  { value: ProfessionalDocumentRejectionReason.EXPIRED, label: 'Documento vencido' },
  { value: ProfessionalDocumentRejectionReason.INCOMPLETE, label: 'Documento incompleto' },
  { value: ProfessionalDocumentRejectionReason.DIVERGENT_DATA, label: 'Dados divergentes' },
  { value: ProfessionalDocumentRejectionReason.WRONG_TYPE, label: 'Tipo de documento incorreto' },
  { value: ProfessionalDocumentRejectionReason.OTHER, label: 'Outro' }
]

export const rejectionReasonLabel = (reason: ProfessionalDocumentRejectionReason): string =>
  REJECTION_REASON_OPTIONS.find((option) => option.value === reason)?.label ?? reason

/** "aaaa-mm-dd" ou ISO → "dd/mm/aaaa" (só a data de calendário, sem conversão de fuso). */
export const formatIsoDate = (iso: string): string => formatIsoDateOnlyBR(iso)

const SAO_PAULO_DAY = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/Sao_Paulo',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit'
})

/**
 * D96: motivo `EXPIRED` com validade hoje ou depois (São Paulo, D10) = o documento ainda vale,
 * mas vence antes do plantão (D91). Sem data, ou com data passada, é vencido de fato.
 */
export const expiresBeforeStation = (
  input: { pendingReason: ProfessionalDocumentPendingReason | null; expiresAt?: string | null },
  now = new Date()
): input is { pendingReason: ProfessionalDocumentPendingReason; expiresAt: string } =>
  input.pendingReason === ProfessionalDocumentPendingReason.EXPIRED &&
  !!input.expiresAt &&
  input.expiresAt.slice(0, 10) >= SAO_PAULO_DAY.format(now)

/** "Vence em dd/mm/aaaa, antes do plantão" (D96). */
export const expiresBeforeStationText = (expiresAt: string): string =>
  `Vence em ${formatIsoDate(expiresAt)}, antes do plantão`

const DATE_TIME = new Intl.DateTimeFormat('pt-BR', {
  timeZone: 'America/Sao_Paulo',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit'
})

/** Instante ISO → "dd/mm/aaaa hh:mm" no horário de São Paulo (eventos e envios, D10). */
export const formatDateTime = (iso: string): string =>
  DATE_TIME.format(new Date(iso)).replace(',', '')

export const HISTORY_KIND_LABEL: Record<DocumentHistoryKind, string> = {
  SUBMITTED: 'Enviado pelo médico',
  APPROVED: 'Aprovado',
  REJECTED: 'Reprovado',
  EXPIRED: 'Vencido',
  UNDONE: 'Decisão desfeita'
}

const DECISION_KINDS: DocumentHistoryKind[] = ['APPROVED', 'REJECTED', 'UNDONE']

/**
 * Autoria do evento do histórico (D26). Ordem: revisor conhecido; versão importada (A5), exceto
 * o "desfazer", que é ato de um revisor; decisão sem revisor = empresa fora do escopo (D80).
 */
export function historyAuthorText(event: DocumentHistoryEvent): string {
  if (event.reviewerName) {
    return event.companyName
      ? `por ${event.reviewerName} (${event.companyName})`
      : `por ${event.reviewerName}`
  }
  if (event.source === ProfessionalDocumentVersionSource.BACKFILL && event.kind !== 'UNDONE') {
    return 'Importado da base da operação'
  }
  return DECISION_KINDS.includes(event.kind) ? 'por outra empresa' : ''
}

export interface PendingReasonInput {
  status: ProfessionalDocumentStatus
  pendingReason: ProfessionalDocumentPendingReason | null
  rejection?: DocumentRejection | null
  expiresAt?: string | null
}

const rejectionText = (rejection: DocumentRejection): string =>
  rejection.reasons
    .map((reason) =>
      reason === ProfessionalDocumentRejectionReason.OTHER && rejection.otherReason
        ? `outro: ${rejection.otherReason}`
        : rejectionReasonLabel(reason).toLowerCase()
    )
    .join(', ')

/** Por que o documento está Pendente (D6): nunca enviado, reprovado com motivo ou vencido. */
export function pendingReasonText(input: PendingReasonInput): string {
  if (input.status !== ProfessionalDocumentStatus.PENDING) return ''
  switch (input.pendingReason) {
    case ProfessionalDocumentPendingReason.NEVER_SENT:
      return 'Nunca enviado'
    case ProfessionalDocumentPendingReason.EXPIRED:
      if (expiresBeforeStation(input)) return expiresBeforeStationText(input.expiresAt)
      return input.expiresAt ? `Vencido em ${formatIsoDate(input.expiresAt)}` : 'Vencido'
    case ProfessionalDocumentPendingReason.REJECTED:
      return input.rejection ? `Reprovado: ${rejectionText(input.rejection)}` : 'Reprovado'
    default:
      return ''
  }
}
